import React from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {fasciaFornitori, loghiFornitori, ricerca, schedeCasino, sezioneAccesa, tuttiIGiochiDellaScheda} from '../../moduli/leggi';
import {giochi as caricaGiochi, giochiPronti, descriviErrore, preferiti as leggiPreferiti} from '../../services/casinoApi';
import RigaScorrevole from '../Casino/RigaScorrevole';
import FasciaFornitori from '../Casino/FasciaFornitori';
import {logoFornitore} from '../Casino/fornitoriNoti';
import GrigliaCelle from './GrigliaCelle';
import Nota from './Nota';
import {percorsoSezione} from '../Casino/comune';

/**
 * CERCA E CATEGORIE (blocco «cercaPillole» della Home): un riquadro, stretto
 * e al centro, con la casella «Cerca giochi» e, sotto, le pastiglie delle
 * categorie.
 *
 * I GIOCHI STANNO SOTTO, SENZA CAMBIARE PAGINA (impostazione «risultati»,
 * di serie accesa): all'apertura si vedono quelli della categoria di
 * partenza (`iniziale`, per esempio «casino:top10»; vuota = la prima
 * pastiglia), come se fosse gia' stata toccata. Toccando un'altra pastiglia
 * i giochi sono quelli della nuova; scrivendo nella casella sono quelli
 * trovati (per nome e, se il modulo Ricerca giochi lo vuole, per studio, nel
 * Live casino solo se il modulo lo vuole). «Mostra di piu'» aggiunge righe;
 * «Vedi tutto» apre la pagina della categoria.
 *
 * TUTTO SU UNA RIGA, COME NELLA LOBBY: a sinistra la casella (corta), in
 * mezzo le categorie su una riga che scorre con una freccia d'oro
 * all'inizio e una alla fine (RigaScorrevole), a destra il menu dei
 * fornitori che hanno giochi di quel tipo (interruttore «Filtro per
 * fornitore») e i Preferiti (i giochi col cuore, di quel tipo). Sopra, le
 * linguette Casino', Live casino e Crash game («Tipo di gioco»); sotto, la
 * fascia dei loghi dei fornitori, che scorre da sola e col mouse: toccando
 * un logo si filtra per quel fornitore. La ricerca, le categorie e i giochi
 * valgono per il tipo scelto e, se scelto, per quel fornitore: una
 * categoria senza giochi di quel fornitore non si vede.
 *
 * CRASH GAME non ha schede sue nel backoffice (la lobby /crash-game usa il
 * modulo del Casino'): la sua linguetta ha una sola pastiglia, «Tutti i crash game»,
 * con tutti i giochi della sezione crashGame del fornitore. Compare solo se
 * la sezione e' accesa e ha giochi.
 *
 * SPENTA l'impostazione, si torna a com'era: la casella con Invio apre la
 * pagina dei risultati (/cerca?q=…) e le pastiglie la lobby sulla scheda.
 *
 * LE PASTIGLIE sono le schede di Casino' e Live casino, con la loro icona,
 * nell'ordine in cui il backoffice le ha scritte (Moduli › Casino' ›
 * Schede). Senza schede, le categorie che manda il fornitore (la lobby, a
 * pastiglie che aprono la lobby). Niente inventato. Al massimo `quante`.
 */
export default function CercaPillole({titolo, casella, quante, risultati, iniziale, colonne, righe, larghezza, tipi = true, perFornitore = true}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [scritto, setScritto] = React.useState('');
  const [categorie, setCategorie] = React.useState([]);
  const conCasino = sezioneAccesa('casino');
  const conLive = sezioneAccesa('liveCasino');
  const conCrash = sezioneAccesa('crashGame');
  const schede = []
      .concat(conCasino ? schedeCasino('casino').map((x) => ({sezione: 'casino', scheda: x, to: '/casino?scheda=' + encodeURIComponent(x.id), icona: x.icona, nome: x.nome, k: 'casino:' + x.id})) : [])
      .concat(conLive ? schedeCasino('liveCasino').map((x) => ({sezione: 'liveCasino', scheda: x, to: '/live-casino?scheda=' + encodeURIComponent(x.id), icona: x.icona, nome: x.nome, k: 'liveCasino:' + x.id})) : []);
  const conSchede = schede.length > 0;
  const conGiochi = risultati && conSchede;

  React.useEffect(() => {
    if (conSchede || !conCasino) return undefined;
    let vivo = true;
    caricaGiochi('casino')
        .then((elenco) => {
          const viste = [];
          elenco.forEach((g) => g.categorie.forEach((c) => { if (!viste.includes(c)) viste.push(c); }));
          if (vivo) setCategorie(viste);
        })
        .catch(() => vivo && setCategorie([]));
    return () => { vivo = false; };
  }, [conSchede, conCasino]);

  const intestazione = String(titolo || '').trim();
  const modulo = ricerca();
  const segnaposto = modulo.segnaposto || t('Cerca giochi');
  const tutti = conCasino ? '/casino' : conLive ? '/live-casino' : '/cerca';
  const stile = Number(larghezza) > 0 ? {maxWidth: Number(larghezza) + 'px', marginLeft: 'auto', marginRight: 'auto', width: '100%'} : undefined;

  /* I giochi di Casino', Live casino e Crash game: gli stessi della lobby, caricati una volta (e tenuti da casinoApi). */
  const sezioniDa = ['casino', 'liveCasino'].filter((k) => sezioneAccesa(k)).concat(conCrash ? ['crashGame'] : []);
  const servono = conGiochi ? sezioniDa.join(',') : '';
  const [stato, setStato] = React.useState(() => {
    const k = servono ? servono.split(',') : [];
    const pronti = k.map((x) => [x, giochiPronti(x)]);
    return k.length && pronti.every(([, e]) => e)
      ? {carica: false, errore: '', elenchi: Object.fromEntries(pronti)}
      : {carica: k.length > 0, errore: '', elenchi: {}};
  });
  React.useEffect(() => {
    if (!servono) return undefined;
    let vivo = true;
    Promise.allSettled(servono.split(',').map((k) => caricaGiochi(k).then((elenco) => [k, elenco])))
        .then((esiti) => {
          if (!vivo) return;
          const andati = esiti.filter((e) => e.status === 'fulfilled').map((e) => e.value);
          if (!andati.length) setStato({carica: false, errore: descriviErrore(esiti[0].reason), elenchi: {}});
          else setStato({carica: false, errore: '', elenchi: Object.fromEntries(andati)});
        });
    return () => { vivo = false; };
  }, [servono]);

  /* Crash game: una pastiglia sola con tutti i giochi della sezione, se ne ha. */
  const crash = conGiochi ? (stato.elenchi.crashGame || []) : [];
  const conTutte = crash.length
    ? schede.concat([{sezione: 'crashGame', scheda: {id: 'crashGame', nome: t('Tutti i crash game'), icona: '🚀', giochi: crash.map((g) => g.id)},
      to: percorsoSezione('crashGame'), icona: '🚀', nome: t('Tutti i crash game'), k: 'crashGame:tutti'}])
    : schede;

  /* Il tipo di gioco (Casino', Live casino, Crash game) e la categoria scelti: all'apertura quelli di `iniziale`, o la prima. */
  const tipiAccesi = ['casino', 'liveCasino', 'crashGame'].filter((k) => sezioneAccesa(k) && conTutte.some((x) => x.sezione === k));
  const [sceltoTipo, setSceltoTipo] = React.useState(null);
  const [scelta, setScelta] = React.useState(null);
  const [fornitore, setFornitore] = React.useState('');
  /* «Preferiti»: al posto della categoria, i giochi col cuore (letti a ogni tocco: il cuore si mette nella lobby). */
  const [soloCuori, setSoloCuori] = React.useState(false);
  const [cuori, setCuori] = React.useState([]);
  const scegliCuori = () => { setScritto(''); setCuori(leggiPreferiti()); setSoloCuori((v) => !v); };
  const dellIniziale = (schede.find((x) => x.k === iniziale) || {}).sezione;
  const tipo = tipiAccesi.includes(sceltoTipo) ? sceltoTipo : (tipiAccesi.includes(dellIniziale) ? dellIniziale : (tipiAccesi[0] || 'casino'));
  const testo = scritto.trim().toLowerCase();

  const delTipo = (stato.elenchi[tipo] || []).map((g) => Object.assign({}, g, {sezione: tipo}));
  const fornitori = Array.from(new Set(delTipo.map((g) => g.fornitore).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const fornitoreVale = perFornitore && fornitori.includes(fornitore) ? fornitore : '';
  const filtrati = fornitoreVale ? delTipo.filter((g) => g.fornitore === fornitoreVale) : delTipo;

  /* Le categorie del tipo scelto; scelto un fornitore, solo quelle che hanno giochi suoi. */
  const delleSchede = conGiochi
    ? conTutte.filter((x) => x.sezione === tipo).filter((x) => stato.carica || !fornitoreVale || tuttiIGiochiDellaScheda(x.scheda, filtrati).length > 0)
    : schede;
  const massimo = Math.max(1, Number(quante) || 10);
  const pillole = (conSchede ? delleSchede : categorie.map((c) => ({to: '/casino?categoria=' + encodeURIComponent(c), icona: '', nome: c, k: 'cat-' + c})))
      .slice(0, conGiochi ? Math.max(massimo, delleSchede.length) : massimo);
  const chiaveScelta = (scelta && pillole.some((p) => p.k === scelta)) ? scelta
    : ((pillole.find((p) => p.k === iniziale) || pillole[0] || {}).k || '');
  const attiva = pillole.find((p) => p.k === chiaveScelta) || null;

  let giochi = [];
  if (conGiochi && !stato.carica) {
    if (testo) {
      giochi = filtrati.filter((g) => g.nome.toLowerCase().includes(testo) || (modulo.studi && (g.fornitore || '').toLowerCase().includes(testo)));
    } else if (soloCuori) {
      giochi = filtrati.filter((g) => cuori.includes(g.id));
    } else if (attiva) {
      giochi = tuttiIGiochiDellaScheda(attiva.scheda, filtrati);
    }
  }
  const apri = (g) => navigate(percorsoSezione(g.sezione) + '/gioco/' + encodeURIComponent(g.id));

  const cerca = (e) => {
    e.preventDefault();
    if (conGiochi) return;
    const q = scritto.trim();
    navigate(q ? '/cerca?q=' + encodeURIComponent(q) : '/cerca');
  };
  const etichettaTipo = (k) => (k === 'liveCasino' ? t('Live casino') : k === 'crashGame' ? t('Crash game') : t('Casinò'));
  const titoloRisultati = testo ? t('{{count}} giochi per «{{testo}}»', {count: giochi.length, testo: scritto.trim()})
    : soloCuori ? '♥ ' + t('Preferiti') : (attiva ? ((attiva.icona ? attiva.icona + ' ' : '') + attiva.nome) : '');
  const paginaCuori = (tipo === 'liveCasino' ? '/live-casino' : '/casino') + '?preferiti=1';
  const barraTipi = conGiochi && tipi && tipiAccesi.length > 1;
  /* La fascia dei loghi sotto la riga: i fornitori del tipo scelto, col logo caricato nel backoffice dove c'e'. */
  const loghi = loghiFornitori();
  const vociFascia = fornitori.map((nome) => ({nome, immagine: logoFornitore(nome, loghi)}));
  const formCerca = casella && (
    <form className={'vet-cp-cerca'} role={'search'} onSubmit={cerca}>
      <svg width={'20'} height={'20'} viewBox={'0 0 24 24'} fill={'none'} stroke={'currentColor'} strokeWidth={'2.2'}
        strokeLinecap={'round'} strokeLinejoin={'round'} aria-hidden={'true'}>
        <circle cx={'11'} cy={'11'} r={'7'}/><line x1={'16.5'} y1={'16.5'} x2={'21'} y2={'21'}/>
      </svg>
      <input type={'search'} value={scritto} onChange={(e) => { setScritto(e.target.value); if (e.target.value) setSoloCuori(false); }}
        placeholder={segnaposto} aria-label={segnaposto}/>
      {/* Coi giochi sotto si cerca mentre si scrive: il tasto «Cerca» non serve. */}
      {!conGiochi && <button type={'submit'} className={'vet-cp-vai'}>{t('Cerca')}</button>}
    </form>
  );

  return (
    <section className={'vet-cerca-pillole' + (conGiochi ? ' con-giochi' : '')} aria-label={intestazione || t('Cerca giochi')} style={stile}>
      <div className={'vet-blocco'}>
        {intestazione && <div className={'vet-testata'}><h2>{intestazione}</h2></div>}
        <div className={'vet-cp-corpo'}>
          {barraTipi && (
            <div className={'vet-cp-barra'}>
              <div className={'vet-cp-tipi'} role={'group'} aria-label={t('Tipo di gioco')}>
                {tipiAccesi.map((k) => (
                  <button key={k} type={'button'} className={'vet-cp-tipo' + (k === tipo ? ' attivo' : '')} aria-pressed={k === tipo}
                    onClick={() => { setSceltoTipo(k); setScelta(null); setFornitore(''); setSoloCuori(false); }}>{etichettaTipo(k)}</button>
                ))}
              </div>
            </div>
          )}
          {conGiochi
            ? (
              <div className={'vet-cp-linea'}>
                {formCerca}
                <RigaScorrevole className={'vet-cp-scorre'} attiva={testo || soloCuori ? '' : chiaveScelta} etichetta={t('Categorie')}>
                  {pillole.map((p) => {
                    const accesa = !testo && !soloCuori && p.k === chiaveScelta;
                    return (
                      <button key={p.k} type={'button'} className={'vet-cp-pillola' + (accesa ? ' attiva' : '')} aria-pressed={accesa}
                        onClick={() => { setScritto(''); setSoloCuori(false); setScelta(p.k); }}>
                        {p.icona && <span aria-hidden={'true'}>{p.icona}</span>}{p.nome}
                      </button>
                    );
                  })}
                </RigaScorrevole>
                {perFornitore && fornitori.length > 0 && (
                  <select className={'vet-cp-fornitore-menu'} value={fornitoreVale} onChange={(e) => setFornitore(e.target.value)} aria-label={t('Fornitore')}>
                    <option value={''}>{t('Tutti i fornitori')}</option>
                    {fornitori.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                )}
                <button type={'button'} className={'vet-cp-cuori' + (soloCuori ? ' attivo' : '')} aria-pressed={soloCuori} onClick={scegliCuori}>
                  {'♥ ' + t('Preferiti')}
                </button>
              </div>
            )
            : (
              <>
                {formCerca}
                <nav className={'vet-cp-pillole lvc-strip'} aria-label={t('Categorie')}>
                  <Link to={tutti} className={'vet-cp-pillola'}><span aria-hidden={'true'}>▦</span>{t('Tutti i giochi')}</Link>
                  {pillole.map((p) => (
                    <Link key={p.k} to={p.to} className={'vet-cp-pillola'}>
                      {p.icona && <span aria-hidden={'true'}>{p.icona}</span>}{p.nome}
                    </Link>
                  ))}
                </nav>
              </>
            )}
          {conGiochi && perFornitore && !stato.carica && vociFascia.length > 1 && (
            <FasciaFornitori className={'vet-cp-fascia'} voci={vociFascia} scelto={fornitoreVale} etichetta={t('Fornitori')} marquee {...fasciaFornitori()}
              onScegli={(v) => setFornitore(v.nome === fornitoreVale ? '' : v.nome)}/>
          )}
        </div>
      </div>
      {conGiochi && (
        <div className={'vet-cp-risultati'}>
          <div className={'vet-cp-titolo'}>
            <h2>{titoloRisultati}</h2>
            {!testo && (soloCuori || attiva) && (
              <button type={'button'} className={'vet-link'} onClick={() => navigate(soloCuori ? paginaCuori : attiva.to)}>
                {t('Vedi tutto')}{giochi.length > 0 && <span className={'vet-griglia-n'}>({giochi.length})</span>}
              </button>
            )}
          </div>
          {stato.errore && <Nota tono={'errore'}>{stato.errore}</Nota>}
          {!stato.carica && !stato.errore && !giochi.length && (
            soloCuori
              ? <Nota>{t('Nessun preferito: tocca il cuore su un gioco per ritrovarlo qui.')}</Nota>
              : testo || fornitoreVale
              ? <Nota>{modulo.vuoto || t('Nessun gioco trovato.')}</Nota>
              : <Nota tono={'spento'}>{t('La scheda «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › {{sezione}} › Schede).', {nome: attiva ? attiva.nome : '', sezione: attiva && attiva.sezione === 'liveCasino' ? t('Live casino') : t('Casinò')})}</Nota>
          )}
          {(stato.carica || giochi.length > 0) && (
            <GrigliaCelle giochi={giochi} carica={stato.carica} righe={Math.max(1, Number(righe) || 2)} colonne={colonne} conAltre
              ripartiDa={(testo ? 'q:' + testo : soloCuori ? '♥' : chiaveScelta) + '|' + fornitoreVale} onApri={apri}
              onTutti={() => (soloCuori ? navigate(paginaCuori) : attiva && navigate(attiva.to))}/>
          )}
        </div>
      )}
    </section>
  );
}
